import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  IconButton,
} from '@mui/material';
import {
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import {
  Restaurant as RestaurantIcon,
  MenuBook as MenuIcon,
  Info as InfoIcon,
  Phone as ContactIcon,
} from '@mui/icons-material';
import api from '../api/api';

interface VendorInfo {
  vendorId: number;
  vendorName: string;
  businessName?: string;
  logoUrl?: string;
  coverImageUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  themePreset?: string;
  defaultQrCode?: string;
  whatsappEnabled?: boolean;
  whatsappNumber?: string;
  // Business contact fields
  address?: string;
  phone?: string;
  cuisineTypes?: string;
  operatingHours?: string;
  tagline?: string;
}

const VendorPublicPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState('home');

  // Refs for scroll navigation
  const homeRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchVendorInfo = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/qr/vendor/${vendorId}/info`);
        setVendorInfo(response.data);
      } catch (err: any) {
        console.error('Failed to fetch vendor info:', err);
        setError(err.response?.status === 404
          ? 'Restaurant not found'
          : 'Failed to load restaurant information');
      } finally {
        setLoading(false);
      }
    };

    if (vendorId) {
      fetchVendorInfo();
    }
  }, [vendorId]);

  const handleOrderNow = () => {
    if (vendorInfo?.defaultQrCode) {
      navigate(`/menu?qr=${vendorInfo.defaultQrCode}`);
    } else {
      navigate(`/menu?vendor=${vendorId}`);
    }
  };

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>, section: string) => {
    setActiveSection(section);
    ref.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Colors from vendor branding or defaults (dark elegant theme)
  const primaryColor = vendorInfo?.primaryColor || '#1a1a1a';
  const secondaryColor = vendorInfo?.secondaryColor || '#d4af37'; // Gold
  const backgroundColor = vendorInfo?.backgroundColor || '#0f0f0f';
  const textColor = vendorInfo?.textColor || '#ffffff';

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f0f0f', /* basil-todo */
          zIndex: 9999,
        }}
      >
        <CircularProgress sx={{ color: '#d4af37' }} /> {/* basil-todo */}
      </Box>
    );
  }

  if (error || !vendorInfo) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#0f0f0f', /* basil-todo */
          color: 'var(--text-inverse)',
          p: 3,
          textAlign: 'center',
          zIndex: 9999,
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>
          {error || 'Restaurant not found'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#888', mb: 3 }}> {/* basil-todo */}
          The restaurant you're looking for doesn't exist or has been removed.
        </Typography>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{ color: 'var(--text-inverse)', borderColor: 'var(--border-subtle)' }}
        >
          Go Home
        </Button>
      </Box>
    );
  }

  // Only use businessName, never show username (which is SOG-specific)
  const displayName = vendorInfo.businessName || 'Restaurant';

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        background: backgroundColor,
        overflow: 'auto',
        zIndex: 9999,
      }}
    >
      {/* Navigation Bar */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 10000,
          background: 'rgba(15, 15, 15, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: `1px solid ${secondaryColor}33`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              py: 1.5,
            }}
          >
            {/* Logo/Name */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {vendorInfo.logoUrl ? (
                <Box
                  component="img"
                  src={vendorInfo.logoUrl}
                  alt={displayName}
                  sx={{ height: 40, width: 'auto', objectFit: 'contain' }}
                />
              ) : (
                <RestaurantIcon sx={{ color: secondaryColor, fontSize: 32 }} />
              )}
              <Typography
                sx={{
                  color: textColor,
                  fontWeight: 700,
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  letterSpacing: '0.05em',
                }}
              >
                {displayName}
              </Typography>
            </Box>

            {/* Navigation Links */}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 3,
              }}
            >
              {[
                { label: 'Home', ref: homeRef, section: 'home' },
                { label: 'Menu', ref: menuRef, section: 'menu' },
                { label: 'About', ref: aboutRef, section: 'about' },
                { label: 'Contact', ref: contactRef, section: 'contact' },
              ].map((item) => (
                <Typography
                  key={item.section}
                  onClick={() => scrollToSection(item.ref, item.section)}
                  sx={{
                    color: activeSection === item.section ? secondaryColor : `${textColor}cc`,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 500,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    transition: 'color 0.3s',
                    '&:hover': { color: secondaryColor },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Order Button */}
            <Button
              variant="contained"
              onClick={handleOrderNow}
              sx={{
                background: secondaryColor,
                color: '#000', /* basil-todo */
                fontWeight: 600,
                px: { xs: 2, sm: 3 },
                py: 1,
                borderRadius: '4px',
                textTransform: 'none',
                '&:hover': {
                  background: `${secondaryColor}dd`,
                },
              }}
            >
              Order Now
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        ref={homeRef}
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: vendorInfo.coverImageUrl
            ? `linear-gradient(rgba(0,0,0,0.6), rgba(0,0,0,0.8)), url(${vendorInfo.coverImageUrl})`
            : `linear-gradient(135deg, ${primaryColor} 0%, ${backgroundColor} 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
          textAlign: 'center',
          px: 3,
          pt: 8,
        }}
      >
        {/* Logo */}
        {vendorInfo.logoUrl && (
          <Box
            component="img"
            src={vendorInfo.logoUrl}
            alt={displayName}
            sx={{
              maxWidth: { xs: '180px', sm: '250px', md: '300px' },
              maxHeight: { xs: '120px', sm: '180px' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              mb: 3,
              filter: 'drop-shadow(0 4px 20px rgba(0,0,0,0.5))',
            }}
          />
        )}

        {/* Restaurant Name */}
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem' },
            fontWeight: 700,
            color: textColor,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            textShadow: '0 4px 20px rgba(0,0,0,0.5)',
            mb: 2,
          }}
        >
          {displayName}
        </Typography>

        {/* Tagline */}
        {vendorInfo.tagline && (
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.25rem' },
              color: `${textColor}cc`,
              fontStyle: 'italic',
              mb: 2,
              maxWidth: '600px',
            }}
          >
            {vendorInfo.tagline}
          </Typography>
        )}

        {/* Cuisine Types */}
        {vendorInfo.cuisineTypes && (
          <Typography
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem' },
              color: secondaryColor,
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              mb: 4,
            }}
          >
            {vendorInfo.cuisineTypes}
          </Typography>
        )}

        {/* Decorative Line */}
        <Box
          sx={{
            width: '80px',
            height: '2px',
            background: secondaryColor,
            mb: 4,
          }}
        />

        {/* CTA Buttons */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleOrderNow}
            sx={{
              background: secondaryColor,
              color: '#000', /* basil-todo */
              fontWeight: 700,
              px: 4,
              py: 1.5,
              fontSize: '1rem',
              borderRadius: '4px',
              textTransform: 'none',
              '&:hover': {
                background: `${secondaryColor}dd`,
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s',
            }}
          >
            View Our Menu
          </Button>
          {vendorInfo.whatsappEnabled && vendorInfo.whatsappNumber && (
            <Button
              variant="contained"
              size="large"
              href={`https://wa.me/${vendorInfo.whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(`Hi! I'd like to order from ${displayName} (takeaway)`)}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                background: '#25D366', /* basil-todo */
                color: 'var(--text-inverse)',
                fontWeight: 700,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: '4px',
                textTransform: 'none',
                '&:hover': {
                  background: 'var(--action)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              Order on WhatsApp
            </Button>
          )}
          {vendorInfo.phone && (
            <Button
              variant="outlined"
              size="large"
              href={`tel:${vendorInfo.phone.split(',')[0].trim()}`}
              sx={{
                borderColor: secondaryColor,
                color: secondaryColor,
                fontWeight: 600,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                borderRadius: '4px',
                textTransform: 'none',
                '&:hover': {
                  borderColor: secondaryColor,
                  background: `${secondaryColor}22`,
                },
              }}
            >
              Call to Order
            </Button>
          )}
        </Box>
      </Box>

      {/* Menu Section */}
      <Box
        ref={menuRef}
        sx={{
          py: { xs: 8, md: 12 },
          px: 3,
          background: backgroundColor,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              color: secondaryColor,
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            Our Menu
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: textColor,
              mb: 3,
            }}
          >
            Delicious Dishes
          </Typography>
          <Box
            sx={{
              width: '60px',
              height: '2px',
              background: secondaryColor,
              mx: 'auto',
              mb: 4,
            }}
          />
          <Typography
            sx={{
              color: `${textColor}99`,
              fontSize: '1rem',
              lineHeight: 1.8,
              mb: 5,
              maxWidth: '500px',
              mx: 'auto',
            }}
          >
            Explore our carefully crafted menu featuring the finest ingredients and authentic flavors.
          </Typography>

          {/* Menu Card */}
          <Box
            onClick={handleOrderNow}
            sx={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${secondaryColor}33`,
              borderRadius: '8px',
              p: { xs: 4, md: 6 },
              cursor: 'pointer',
              transition: 'all 0.3s',
              '&:hover': {
                background: 'rgba(255,255,255,0.06)',
                borderColor: secondaryColor,
                transform: 'translateY(-4px)',
              },
            }}
          >
            <MenuIcon sx={{ fontSize: 60, color: secondaryColor, mb: 2 }} />
            <Typography
              sx={{
                color: textColor,
                fontSize: '1.5rem',
                fontWeight: 600,
                mb: 2,
              }}
            >
              Browse Full Menu
            </Typography>
            <Typography
              sx={{
                color: `${textColor}77`,
                fontSize: '0.9rem',
              }}
            >
              Click to view our complete menu with prices
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* About Section */}
      <Box
        ref={aboutRef}
        sx={{
          py: { xs: 8, md: 12 },
          px: 3,
          background: `linear-gradient(180deg, ${primaryColor} 0%, ${backgroundColor} 100%)`,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography
            sx={{
              color: secondaryColor,
              fontSize: '0.85rem',
              fontWeight: 600,
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              mb: 2,
            }}
          >
            About Us
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
              fontWeight: 700,
              color: textColor,
              mb: 3,
            }}
          >
            Our Story
          </Typography>
          <Box
            sx={{
              width: '60px',
              height: '2px',
              background: secondaryColor,
              mx: 'auto',
              mb: 4,
            }}
          />
          <Typography
            sx={{
              color: `${textColor}cc`,
              fontSize: { xs: '1rem', sm: '1.1rem' },
              lineHeight: 1.9,
              maxWidth: '600px',
              mx: 'auto',
            }}
          >
            Welcome to {displayName}! We take pride in serving authentic {vendorInfo.cuisineTypes || 'cuisine'} made with the freshest ingredients and traditional recipes passed down through generations. Every dish is prepared with love and care to bring you an unforgettable dining experience.
          </Typography>
        </Container>
      </Box>

      {/* Contact Section */}
      <Box
        ref={contactRef}
        sx={{
          py: { xs: 8, md: 12 },
          px: 3,
          background: backgroundColor,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              sx={{
                color: secondaryColor,
                fontSize: '0.85rem',
                fontWeight: 600,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                mb: 2,
              }}
            >
              Contact Us
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                color: textColor,
                mb: 3,
              }}
            >
              Get In Touch
            </Typography>
            <Box
              sx={{
                width: '60px',
                height: '2px',
                background: secondaryColor,
                mx: 'auto',
              }}
            />
          </Box>

          {/* Contact Cards */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {/* Hours */}
            {vendorInfo.operatingHours && (
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${secondaryColor}33`,
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `${secondaryColor}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <ClockIcon style={{ width: 28, height: 28, color: secondaryColor }} />
                </Box>
                <Typography
                  sx={{
                    color: textColor,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Opening Hours
                </Typography>
                <Typography
                  sx={{
                    color: `${textColor}99`,
                    fontSize: '0.95rem',
                  }}
                >
                  {vendorInfo.operatingHours}
                </Typography>
              </Box>
            )}

            {/* Location */}
            {vendorInfo.address && (
              <Box
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${secondaryColor}33`,
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `${secondaryColor}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <MapPinIcon style={{ width: 28, height: 28, color: secondaryColor }} />
                </Box>
                <Typography
                  sx={{
                    color: textColor,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Location
                </Typography>
                <Typography
                  sx={{
                    color: `${textColor}99`,
                    fontSize: '0.95rem',
                  }}
                >
                  {vendorInfo.address}
                </Typography>
              </Box>
            )}

            {/* Phone */}
            {vendorInfo.phone && (
              <Box
                component="a"
                href={`tel:${vendorInfo.phone.split(',')[0].trim()}`}
                sx={{
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${secondaryColor}33`,
                  borderRadius: '8px',
                  p: 4,
                  textAlign: 'center',
                  textDecoration: 'none',
                  transition: 'all 0.3s',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.06)',
                    borderColor: secondaryColor,
                  },
                }}
              >
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    background: `${secondaryColor}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <PhoneIcon style={{ width: 28, height: 28, color: secondaryColor }} />
                </Box>
                <Typography
                  sx={{
                    color: textColor,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Call Us
                </Typography>
                <Typography
                  sx={{
                    color: `${textColor}99`,
                    fontSize: '0.95rem',
                  }}
                >
                  {vendorInfo.phone}
                </Typography>
              </Box>
            )}
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        sx={{
          py: 4,
          px: 3,
          background: primaryColor,
          borderTop: `1px solid ${secondaryColor}33`,
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {vendorInfo.logoUrl ? (
                <Box
                  component="img"
                  src={vendorInfo.logoUrl}
                  alt={displayName}
                  sx={{ height: 30, width: 'auto', objectFit: 'contain' }}
                />
              ) : (
                <RestaurantIcon sx={{ color: secondaryColor, fontSize: 24 }} />
              )}
              <Typography
                sx={{
                  color: textColor,
                  fontWeight: 600,
                  fontSize: '1rem',
                }}
              >
                {displayName}
              </Typography>
            </Box>

            <Typography
              sx={{
                color: `${textColor}66`,
                fontSize: '0.85rem',
              }}
            >
              Powered by{' '}
              <Box
                component="span"
                sx={{ color: secondaryColor, fontWeight: 600 }}
              >
                Scan Order & Go
              </Box>
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default VendorPublicPage;
